import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';

import { STORAGE_KEYS } from '@/constants/storageKeys';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '@/util/platformStorage';

/**
 * TEMPORARY dev switch between the two case summary layouts in the inquiry chat, so they can be
 * compared in the same app. Delete this file and the `useCaseLayout` calls once one layout is
 * chosen. No deploy and no flag service.
 *
 * - `current`: the white "Case summary" card above the questions.
 * - `recommended`: the design review's one outlined bubble with the white summary section. The default.
 *
 * Web: `?caseLayout=recommended` in the URL, remembered in localStorage so it survives
 * navigation, and `?caseLayout=current` clears it. Native has no picker since it left Developer
 * settings, so it keeps whatever is stored, or the default.
 */
export type CaseLayout = 'current' | 'recommended';

const CASE_LAYOUTS: readonly CaseLayout[] = ['current', 'recommended'];

export const CASE_LAYOUT_QUERY_PARAM = 'caseLayout';

export function parseCaseLayout(value: string | null | undefined): CaseLayout | undefined {
  return CASE_LAYOUTS.find((layout) => layout === value);
}

/** The URL's choice wins over the remembered one, and `recommended` when neither is set. */
export function resolveCaseLayout(
  search: string | null | undefined,
  stored: string | null | undefined
): CaseLayout {
  const fromUrl = parseCaseLayout(new URLSearchParams(search ?? '').get(CASE_LAYOUT_QUERY_PARAM));

  return fromUrl ?? parseCaseLayout(stored) ?? 'recommended';
}

type CaseLayoutStore = {
  layout: CaseLayout;
  setLayout: (layout: CaseLayout) => void;
};

function readInitialWebLayout(): CaseLayout {
  if (typeof window === 'undefined') {
    return 'recommended';
  }

  const layout = resolveCaseLayout(
    window.location.search,
    safeLocalStorageGetItem(STORAGE_KEYS.CASE_LAYOUT_DEBUG)
  );

  safeLocalStorageSetItem(STORAGE_KEYS.CASE_LAYOUT_DEBUG, layout);

  return layout;
}

export const useCaseLayoutStore = create<CaseLayoutStore>((set) => ({
  layout: isWeb ? readInitialWebLayout() : 'recommended',
  setLayout: (layout) => {
    set({ layout });

    if (isWeb) {
      safeLocalStorageSetItem(STORAGE_KEYS.CASE_LAYOUT_DEBUG, layout);
    } else {
      AsyncStorage.setItem(STORAGE_KEYS.CASE_LAYOUT_DEBUG, layout).catch(() => undefined);
    }
  },
}));

if (!isWeb) {
  AsyncStorage.getItem(STORAGE_KEYS.CASE_LAYOUT_DEBUG)
    .then((stored) => {
      const layout = parseCaseLayout(stored);

      if (layout) {
        useCaseLayoutStore.setState({ layout });
      }
    })
    .catch(() => undefined);
}

export function useCaseLayout(): CaseLayout {
  return useCaseLayoutStore((state) => state.layout);
}

export function isRecommendedCaseLayout(layout: CaseLayout): boolean {
  return layout !== 'current';
}
